import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../../api/client'
import type { WorkflowJob } from '../../api/types'

export default function CompanyWorkflows({ companyId, onChanged, history = false }: { companyId: string; onChanged: () => Promise<void>; history?: boolean }) {
  const [jobs, setJobs] = useState<WorkflowJob[]>([])
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [refresh, setRefresh] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [actionError, setActionError] = useState('')
  const [notice, setNotice] = useState('')
  const [busy, setBusy] = useState('')
  const alive = useRef(true)
  const actionInFlight = useRef(false)
  const limit = 20
  useEffect(() => { alive.current = true; return () => { alive.current = false } }, [])
  useEffect(() => {
    let live = true
    let timer: ReturnType<typeof setTimeout> | undefined
    async function poll() {
      try {
        const result = await api.workflows(companyId, limit, offset)
        if (live) { setJobs(result.jobs); setTotal(result.total); setLoadError('') }
      } catch (error) {
        if (live) setLoadError(error instanceof Error ? error.message : 'Could not load processing history.')
      } finally {
        if (live) { setLoading(false); timer = setTimeout(() => { void poll() }, 4000) }
      }
    }
    void poll()
    return () => { live = false; clearTimeout(timer) }
  }, [companyId, offset, refresh])
  async function recover(job: WorkflowJob, action: 'retry' | 'cancel') {
    if (actionInFlight.current) return
    actionInFlight.current = true
    setBusy(job.id); setActionError(''); setNotice('')
    try {
      await api.recoverWorkflow(job.id, action)
      if (alive.current) {
        setNotice(action === 'retry' ? 'Retry queued. Follow its progress here.' : 'Workflow canceled.')
        setRefresh(value => value + 1)
      }
      if (alive.current) await onChanged()
    } catch (error) {
      if (alive.current) setActionError(error instanceof Error ? error.message : 'Could not update this workflow. Check its current status before retrying.')
    } finally {
      actionInFlight.current = false
      if (alive.current) setBusy('')
    }
  }
  return <section className="panel" style={{ marginBottom: '1.5rem' }} aria-labelledby="company-processing-heading">
    <h2 id="company-processing-heading">{history ? 'Processing history' : 'Current progress'}</h2>
    <p className="muted">{history ? 'Recorded attempts and recovery actions for this company.' : 'Latest attempt and active work. Delivery is shown with each engagement; older attempts are in Diagnostics.'}</p>
    {loadError && <div className="notice crit" role="alert">{loadError} <button className="btn small secondary" disabled={loading} onClick={() => { setLoading(true); setRefresh(value => value + 1) }}>Retry loading</button></div>}
    {actionError && <div className="notice crit" role="alert">{actionError}</div>}
    {notice && <p className="notice" role="status">{notice}</p>}
    {loading ? <p className="muted">Loading processing history…</p> : <>
      {!jobs.length && <p className="muted">{offset ? 'No attempts on this page.' : 'No workflows yet. Choose a preparation or contact action above to begin.'}</p>}
      {jobs.filter((job,index) => history || index === 0 || ['queued','running'].includes(job.status)).map(job => <article className="workflow-attempt" key={job.id}>
        <div className="workflow-meta">
          <span className={`pill ${job.status === 'completed' ? 'ok' : ['blocked', 'failed'].includes(job.status) ? 'warn' : ''}`}>{job.status.replace(/_/g, ' ')}</span>
          <span>{job.action.replace(/_/g, ' ')} · {job.stage.replace(/_/g, ' ')}</span>
          <time className="muted" dateTime={job.updated_at}>{new Date(job.updated_at).toLocaleString()}</time>
        </div>
        {job.error && <div className="workflow-error"><p>{job.error}</p>{job.error.toLowerCase().includes('spend') && <Link to="/app/spend">Review spend controls</Link>}</div>}
        {['blocked', 'failed', 'queued'].includes(job.status) && <div className="action-group">
          {job.dispatch_state ? <p className="muted">Review the recorded provider outcome before retrying or canceling.</p> : <>
            {job.status !== 'queued' && <button className="btn small secondary" disabled={!!busy} onClick={() => { void recover(job, 'retry') }}>{busy === job.id ? 'Updating…' : 'Retry'}</button>}
            <button className="btn small secondary" disabled={!!busy} onClick={() => { void recover(job, 'cancel') }}>Cancel</button>
          </>}
        </div>}
      </article>)}
    </>}
    {history && (total > limit || offset > 0) && <div className="toolbar" style={{ marginTop: '1rem' }}>
      <button className="btn small secondary" disabled={!offset || loading || !!busy} onClick={() => { setLoading(true); setOffset(value => Math.max(0, value - limit)) }}>Previous</button>
      <span>{total ? Math.min(offset + 1, total) : 0}–{Math.min(offset + limit, total)} of {total} attempts</span>
      <button className="btn small secondary" disabled={offset + limit >= total || loading || !!busy} onClick={() => { setLoading(true); setOffset(value => value + limit) }}>Next</button>
    </div>}
  </section>
}
